import { useCallback, useMemo } from 'react';

import type { I18nUtilities } from '@change-corgi/core-react/i18n';
import { useI18n } from '@change-corgi/core-react/i18n';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import type { ShareChannel } from '#src/app/pages/psf/promoteOrShare/shared/types';
import type { FacebookGroupRecommendation } from '#src/app/pages/psf/shared/api';
import type { ShareAction } from '#src/app/shared/hooks/share';

import { usePromoteOrShareActionUrls } from '../usePromoteOrShareActionUrls';

import { mapButtonConfigs } from './mapButtonConfigs';
import { usePsfShareAvailableShareActions } from './share';
import { type ChannelAwareCta, useChannelAwareCta } from './useChannelAwareCta';
import {
	type FacebookPostSheetTap,
	type UseFacebookPostSheetResult,
	useFacebookPostSheet,
} from './useFacebookPostSheet';
import { useFacebookPostSheetEligibility } from './useFacebookPostSheetEligibility';
import { useFacebookPostSheetInterception } from './useFacebookPostSheetInterception';
import { useTreatFacebookCtaIntlExperiment } from './useTreatFacebookCtaIntlExperiment';
import { type FacebookPostSheetArm, useTreatFacebookPostSheetExperiment } from './useTreatFacebookPostSheetExperiment';
import { useTreatWhatsappCtaExperiment } from './useTreatWhatsappCtaExperiment';

type TranslateLabel = (i18n: I18nUtilities) => string;

type ButtonConfig = {
	ShareAction: ShareAction['ShareAction'];
	channel: ShareChannel;
	label: TranslateLabel;
	successLabel?: TranslateLabel;
	secondaryLabel?: TranslateLabel;
	url: string;
	shortUrl?: string;
};

type UseActionItemsResult = ModelHookResult<
	{
		buttonConfigs: readonly ButtonConfig[];
		continueButtonProps: {
			label: string;
			action: () => void;
		};
		shareVisualUrl: string | undefined;
		channelAwareCta: ChannelAwareCta;
		facebookPostSheet: UseFacebookPostSheetResult;
		/** The sheet a treated Facebook tap opens, or `null` for production. `ShareSlideUp` renders its steps. */
		facebookPostSheetArm: FacebookPostSheetArm | null;
		/** The group recommendations the `variant_2` group cards list, from the fetch that eligibility read. */
		facebookPostSheetGroups: readonly FacebookGroupRecommendation[];
	},
	{
		handleOpenShareSlideUp: () => void;
		handleCloseShareSlideUp: () => void;
	}
>;

type Params = {
	/** Opens the Facebook post sheet for a treated Facebook tap. `ShareSlideUp` owns the sheet's open state. */
	onOpenFacebookPostSheet?: (tap: FacebookPostSheetTap) => void;
};

// oxlint-disable-next-line max-statements
export function useShareActions({ onOpenFacebookPostSheet }: Params = {}): UseActionItemsResult {
	const { shareActions: availableShareActions, shareOptions } = usePsfShareAvailableShareActions();
	const {
		data: { petition, isShareAssetsEnabled },
	} = usePsfPromoteOrSharePageData();
	const { treatOnSlideUpOpen: treatWhatsappCtaOnSlideUpOpen, whatsappCtaLayout } = useTreatWhatsappCtaExperiment();
	const { treatOnSlideUpOpen: treatFacebookCtaIntlOnSlideUpOpen, facebookCtaLayout } =
		useTreatFacebookCtaIntlExperiment();
	const channelAwareCta = useChannelAwareCta(whatsappCtaLayout, facebookCtaLayout);
	const mappedButtonConfigs = useMemo(() => mapButtonConfigs(availableShareActions), [availableShareActions]);

	const shareVisualUrl = isShareAssetsEnabled ? petition.bestShareVisual?.url : undefined;

	const {
		isEligible: isFacebookPostDataEligible,
		caption: facebookPostCaption,
		groups: facebookPostGroups,
		startFetch: startFacebookPostDataFetch,
		reportTap: reportFacebookTap,
	} = useFacebookPostSheetEligibility(petition.slug);
	const facebookPostSheet = useFacebookPostSheet({ caption: facebookPostCaption, shareOptions });
	const {
		sheetArm: facebookPostSheetArm,
		treatsAtTap: treatsFacebookPostSheetAtTap,
		treatAtTap: treatFacebookPostSheetAtTap,
	} = useTreatFacebookPostSheetExperiment(isFacebookPostDataEligible);

	const { copyAtTap: copyFacebookPostAtTap, tapFacebook, shareAtTap: shareFacebookAtTap } = facebookPostSheet;
	const { onBeforeShare } = shareOptions;
	const onFacebookTap = useCallback(() => onBeforeShare?.('facebook'), [onBeforeShare]);
	const onFacebookPostSheetTap = useCallback(
		(tap: FacebookPostSheetTap) => {
			tapFacebook(tap);
			onOpenFacebookPostSheet?.(tap);
		},
		[tapFacebook, onOpenFacebookPostSheet],
	);
	const buttonConfigsWithFacebookPostSheet = useFacebookPostSheetInterception(
		mappedButtonConfigs,
		useMemo(
			() => ({
				onTap: onFacebookTap,
				treatAtTap: treatFacebookPostSheetAtTap,
				reportTap: reportFacebookTap,
				// Eligibility already requires a ready caption, so this only guards a missing Facebook link.
				sheetArm: facebookPostSheet.caption !== null ? facebookPostSheetArm : null,
				// Same guard: `shareAtTap` needs the Facebook link, and the pipeline runs without it.
				treatsAtTap: facebookPostSheet.caption !== null && treatsFacebookPostSheetAtTap,
				shareAtTap: shareFacebookAtTap,
				// EXPERIMENT: psf_promote_or_share_facebook_post_sheet_experiment. Both sheets copy at the Facebook
				// tap, for the copied pill they open with.
				copyAtTap: copyFacebookPostAtTap,
				onSheetTap: onFacebookPostSheetTap,
			}),
			[
				onFacebookTap,
				treatFacebookPostSheetAtTap,
				reportFacebookTap,
				facebookPostSheet.caption,
				facebookPostSheetArm,
				treatsFacebookPostSheetAtTap,
				shareFacebookAtTap,
				copyFacebookPostAtTap,
				onFacebookPostSheetTap,
			],
		),
	);

	return {
		data: {
			buttonConfigs: buttonConfigsWithFacebookPostSheet,
			continueButtonProps: useContinueButtonProps(),
			shareVisualUrl,
			channelAwareCta,
			facebookPostSheet,
			facebookPostSheetArm,
			facebookPostSheetGroups: facebookPostGroups,
		},
		actions: useHandlers(treatWhatsappCtaOnSlideUpOpen, treatFacebookCtaIntlOnSlideUpOpen, startFacebookPostDataFetch),
	};
}

function useHandlers(
	treatWhatsappCtaOnSlideUpOpen: () => Promise<void>,
	treatFacebookCtaIntlOnSlideUpOpen: () => Promise<void>,
	startFacebookPostDataFetch: () => void,
) {
	const track = useTracking();

	return {
		handleOpenShareSlideUp: useCallback(() => {
			void track('combo_psf_share');
			void track('combo_psf_share_slide_up_opened');
			// Channel-aware CTA, Top-8 relaunch (CHANGE-99290): treat + $exposure coupled to the slide-up
			// open, so treated == exposed == slide-up openers. See useTreatWhatsappCtaExperiment.
			void treatWhatsappCtaOnSlideUpOpen();
			// Same coupling for Facebook. The two are mutually exclusive by arrival channel, so at most
			// one of these enrolls a given signer.
			void treatFacebookCtaIntlOnSlideUpOpen();

			startFacebookPostDataFetch();
		}, [track, treatWhatsappCtaOnSlideUpOpen, treatFacebookCtaIntlOnSlideUpOpen, startFacebookPostDataFetch]),
		handleCloseShareSlideUp: useCallback(() => {
			void track('combo_psf_share_slide_up_closed');
		}, [track]),
	};
}

function useContinueButtonProps() {
	const track = useTracking();
	const { translate } = useI18n();
	const navigate = useNavigate();
	const {
		data: { shareButtonUrl: continueUrl },
	} = usePromoteOrShareActionUrls();

	return {
		label: translate('corgi.psf.share.footer.continue'),
		action: useCallback(() => {
			void track('combo_psf_share_slide_up_continue');
			navigate(continueUrl);
		}, [continueUrl, navigate, track]),
	};
}
